import {
  createApiRef,
  createApiFactory,
  discoveryApiRef,
  fetchApiRef,
  DiscoveryApi, FetchApi
} from '@backstage/core-plugin-api';
import {
  PLUGIN_ID,
  PLUGIN_NAME,
  API_ENDPOINTS,
  HTTP_HEADERS,
  HTTP_METHODS,
  ERROR_MESSAGES,
} from './constants';

export interface DeployData {
  service: string;
  releaseId: number;
}

export interface Release {
  id: number;
  service: string;
  version_name: string;
  container_reference: string;
  additional_data: any;
  created_at: string;
  is_running: boolean;
}

export interface KubernetesReleaseApi {
  getReleases(serviceId: string): Promise<Release[]>;
  deploy(deployData: DeployData): Promise<any>;
  getChanges(serviceId: string): Promise<any[]>;
}

export const kubernetesReleaseApiRef = createApiRef<KubernetesReleaseApi>({
  id: PLUGIN_ID,
});

class KubernetesReleaseClient implements KubernetesReleaseApi {
 
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl() {
    return await this.discoveryApi.getBaseUrl(PLUGIN_NAME);
  }

  async getReleases(serviceId: string): Promise<Release[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${API_ENDPOINTS.RELEASES}/${serviceId}`);
    if (!response.ok) {
      throw new Error(`${ERROR_MESSAGES.FETCH_RELEASES}: ${response.statusText}`);
    }
    return await response.json();
  }

  async deploy(deployData: DeployData): Promise<any> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${API_ENDPOINTS.DEPLOY}`, {
      method: HTTP_METHODS.POST,
      headers: {
        'Content-Type': HTTP_HEADERS.CONTENT_TYPE_JSON,
      },
      body: JSON.stringify(deployData),
    });
    if (!response.ok) {
      throw new Error(`${ERROR_MESSAGES.DEPLOY_RELEASE}: ${response.statusText}`);
    }
    return await response.json();
  }

  async getChanges(serviceId: string): Promise<any[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${API_ENDPOINTS.CHANGES}/${serviceId}`);
    if (!response.ok) {
      throw new Error(`${ERROR_MESSAGES.FETCH_CHANGES}: ${response.statusText}`);
    }
    return await response.json();
  }


}

export const kubernetesReleaseApi = createApiFactory({
  api: kubernetesReleaseApiRef,
  deps: {
    discoveryApi: discoveryApiRef,
    fetchApi: fetchApiRef,
  },
  factory: ({ discoveryApi, fetchApi }) =>
    new KubernetesReleaseClient({ discoveryApi, fetchApi }),
});